import type {
  ClientProposalTemplateJSON,
  UpdateClientProposalTemplateRequest,
} from "work/client-proposal-templates/_schema/client-proposal-template-schema";

/**
 * The configuration as the screen holds it while somebody is editing.
 *
 * Every selector carries `""` for "not chosen" rather than `null`, because that is what a MUI
 * `select` gives back when its empty option is picked and a controlled field that is sometimes
 * null is an uncontrolled field half the time. `discountPercent` is the string the reader typed,
 * not a number: parsing on each keystroke turns "1." into 1 and puts the cursor back a character.
 */
export type ClientProposalTemplateFormState = {
  feeScheduleTemplateId: string;
  teamTemplateId: string;
  conflictsTemplateDocumentId: string;
  policyTemplateDocumentIds: string[];
  discountPercent: string;
  negotiable: boolean;
  conflictsCheckWaived: boolean;
  autoCreateEnabled: boolean;
};

/** What a client who has configured nothing starts from. */
export const EMPTY_CLIENT_PROPOSAL_TEMPLATE_FORM: ClientProposalTemplateFormState = {
  feeScheduleTemplateId: "",
  teamTemplateId: "",
  conflictsTemplateDocumentId: "",
  policyTemplateDocumentIds: [],
  discountPercent: "0",
  negotiable: true,
  conflictsCheckWaived: false,
  autoCreateEnabled: false,
};

/**
 * The most decimal places a percentage is kept to.
 *
 * The API stores a fraction, so a percentage is divided by a hundred before it is sent, and binary
 * floating point makes 12.3 / 100 into 0.12300000000000001. Rounded here rather than left for the
 * server, because the server would store it and hand it back — and the screen would then show a
 * discount nobody typed.
 */
const DISCOUNT_FRACTION_PRECISION = 6;

export function toFormState(
  configuration: ClientProposalTemplateJSON | undefined,
): ClientProposalTemplateFormState {
  if (!configuration) return EMPTY_CLIENT_PROPOSAL_TEMPLATE_FORM;
  return {
    feeScheduleTemplateId: configuration.feeScheduleTemplateId ?? "",
    teamTemplateId: configuration.teamTemplateId ?? "",
    conflictsTemplateDocumentId: configuration.conflictsTemplateDocumentId ?? "",
    policyTemplateDocumentIds: [...configuration.policyTemplateDocumentIds],
    discountPercent: toDiscountPercent(configuration.discount),
    negotiable: configuration.negotiable,
    conflictsCheckWaived: configuration.conflictsCheckWaived,
    autoCreateEnabled: configuration.autoCreateEnabled,
  };
}

/** A stored fraction as the percentage the screen shows, without a trailing `.0`. */
export function toDiscountPercent(discount: number): string {
  return String(Number((discount * 100).toFixed(DISCOUNT_FRACTION_PRECISION)));
}

/**
 * Why the typed discount cannot be sent, or undefined when it can.
 *
 * Checked here as well as on the server, and for a different reason: the API refuses a fraction
 * outside 0 to 1 with one sentence about the whole configuration, which arrives after a round trip
 * and says nothing about which field it means.
 */
export function describeDiscountProblem(discountPercent: string): string | undefined {
  const typed = discountPercent.trim();
  if (!typed) return "Enter a discount, or 0 for none.";
  const percent = Number(typed);
  if (!Number.isFinite(percent)) return "A discount is a number of percent.";
  if (percent < 0 || percent > 100) return "A discount is between 0 and 100 percent.";
  return undefined;
}

/**
 * The whole record, as the API takes it.
 *
 * Every field travels, including the ones nobody touched, because the endpoint replaces rather
 * than patches. An unchosen selector is sent as null: the record is allowed to be half configured
 * — a client sets this up over more than one sitting — and the gap it reports back is how the
 * screen says so.
 */
export function toUpdateRequest(
  state: ClientProposalTemplateFormState,
): UpdateClientProposalTemplateRequest {
  return {
    feeScheduleTemplateId: state.feeScheduleTemplateId || null,
    teamTemplateId: state.teamTemplateId || null,
    conflictsTemplateDocumentId: state.conflictsTemplateDocumentId || null,
    policyTemplateDocumentIds: [...state.policyTemplateDocumentIds],
    discount: toDiscountFraction(state.discountPercent),
    negotiable: state.negotiable,
    conflictsCheckWaived: state.conflictsCheckWaived,
    autoCreateEnabled: state.autoCreateEnabled,
  };
}

/** The typed percentage as the fraction between 0 and 1 the API stores. */
export function toDiscountFraction(discountPercent: string): number {
  const percent = Number(discountPercent.trim());
  if (!Number.isFinite(percent)) return 0;
  return Number((percent / 100).toFixed(DISCOUNT_FRACTION_PRECISION));
}

/**
 * Whether the screen is holding an edit that has not been sent.
 *
 * Compared against the configuration the API last returned rather than tracked as a dirty flag,
 * so a reader who types a change and puts it back reads as unchanged — which is what stops the
 * page warning about work nobody did.
 */
export function hasUnsavedChanges(
  state: ClientProposalTemplateFormState,
  configuration: ClientProposalTemplateJSON | undefined,
): boolean {
  const saved = toFormState(configuration);
  return (
    state.feeScheduleTemplateId !== saved.feeScheduleTemplateId ||
    state.teamTemplateId !== saved.teamTemplateId ||
    state.conflictsTemplateDocumentId !== saved.conflictsTemplateDocumentId ||
    state.policyTemplateDocumentIds.join(",") !== saved.policyTemplateDocumentIds.join(",") ||
    toDiscountFraction(state.discountPercent) !== toDiscountFraction(saved.discountPercent) ||
    state.negotiable !== saved.negotiable ||
    state.conflictsCheckWaived !== saved.conflictsCheckWaived ||
    state.autoCreateEnabled !== saved.autoCreateEnabled
  );
}
